import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { useRef } from "react";
import { toast } from "sonner";
import { clientService } from "@/lib/api/services/client.service";
import type { Database } from "@/lib/supabase/supabase-types";

type ClientProfile = Database["public"]["Tables"]["client_profiles"]["Row"];
type ClientProfileInsert = Database["public"]["Tables"]["client_profiles"]["Insert"];
type ClientProfileUpdate = Database["public"]["Tables"]["client_profiles"]["Update"];

/**
 * Hook for managing client profiles
 * 
 * @param clientProfileId - The client profile ID (UUID from postgres)
 * @param options - Optional configuration
 * @param options.enableMutations - Whether to enable create/update mutations (default: false)
 */
export function useClientProfile(
  clientProfileId: string | null | undefined,
  options: { enableMutations?: boolean } = {}
) {
  const { user } = useUser();
  const queryClient = useQueryClient();
  // Ref to track if we should suppress the creation toast (e.g., during onboarding)
  const suppressCreateToastRef = useRef(false);

  // Get the profile data
  // Only enable query when:
  // 1. clientProfileId is a string (fetch specific profile), OR
  // 2. clientProfileId is undefined (fetch current user's profile), BUT NOT when it's null (user is not a client)
  const shouldFetch = clientProfileId !== null && (!!clientProfileId || !!user?.id);
  const profileQuery = useQuery({
    queryKey: ["client-profile", clientProfileId],
    queryFn: async () => {
      // If no profileId provided, try to get current user's profile via API
      if (!clientProfileId) {
        if (user?.id) {
          try {
            return await clientService.getMyProfile();
          } catch (err: any) {
            if (err.code === 'NOT_FOUND' || err.status === 404) {
              throw new Error('Profile not found');
            }
            throw err;
          }
        }
        throw new Error("No profile ID or user");
      }

      // Use backend API for all profile fetching (public endpoint)
      try {
        // Check if it's the current user's profile first
        if (user?.id) {
          try {
            const myProfile = await clientService.getMyProfile();
            if (myProfile.id === clientProfileId) {
              return myProfile;
            }
          } catch {
            // Not current user's profile, continue to public endpoint
          }
        }

        // Use public endpoint to get profile by ID
        return await clientService.getProfile(clientProfileId);
      } catch (err: any) {
        if (err.code === 'NOT_FOUND' || err.status === 404) {
          throw new Error('Profile not found');
        }
        throw err;
      }
    },
    enabled: shouldFetch,
    staleTime: 60 * 1000, // 1 minute
  });

  // Determine ownership
  const profile = profileQuery.data;
  // User is owner if profile belongs to them OR if no profile exists yet (so they can create one)
  const isOwner = profile ? profile.user_id === user?.id : !!user?.id;

  // Create profile mutation (only for owners)
  const createProfileMutation = useMutation({
    mutationFn: async (profileData: Omit<ClientProfileInsert, 'user_id'>) => {
      if (!user?.id) throw new Error("No user ID");

      return await clientService.createProfile(profileData);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["client-profile", data.id], data);
      // Invalidate client profile ID query so useRole gets the new profile ID
      queryClient.invalidateQueries({ queryKey: ["client-profile-id", user?.id] });
      // Invalidate the admin list of client profiles
      queryClient.invalidateQueries({ queryKey: ["client-profiles"] });
      // Also set the profile ID directly in cache for immediate availability
      queryClient.setQueryData(["client-profile-id", user?.id], data.id);
      
      // Only show toast if not suppressed (e.g., during onboarding)
      if (!suppressCreateToastRef.current) {
        toast.success('Client profile created');
      }
      suppressCreateToastRef.current = false;
    },
    onError: (error: any) => {
      console.error('Failed to create client profile:', error);
      toast.error(error.message || 'Failed to create client profile');
    },
  });

  // Update profile mutation (only for owners)
  const updateProfileMutation = useMutation({
    mutationFn: async (updates: ClientProfileUpdate) => {
      if (!user?.id) throw new Error("No user ID");

      return await clientService.updateProfile(updates);
    },
    onMutate: async (updates) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["client-profile", clientProfileId] });

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData<ClientProfile>(["client-profile", clientProfileId]);

      // Optimistically update to the new value
      if (previousProfile) {
        const optimisticData = {
          ...previousProfile,
          ...updates,
        };
        queryClient.setQueryData<ClientProfile>(["client-profile", clientProfileId], optimisticData);
      }

      // Return a context object with the snapshotted value
      return { previousProfile };
    },
    onError: (err, _updates, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousProfile) {
        queryClient.setQueryData(["client-profile", clientProfileId], context.previousProfile);
      }
      console.error('Client profile update failed:', err);
      toast.error('Failed to update client profile. Please try again.');
    },
    onSuccess: (data) => {
      // Update with the actual server data
      queryClient.setQueryData(["client-profile", clientProfileId], data);
      // Invalidate the admin list of client profiles to keep it in sync
      queryClient.invalidateQueries({ queryKey: ["client-profiles"] });
      toast.success('Client profile updated');
    },
  });

  // Helper to create or update profile (only available if mutations enabled and is owner)
  const saveProfile = async (
    data: Omit<ClientProfileInsert, 'user_id'> | ClientProfileUpdate,
    saveOptions?: { suppressToast?: boolean }
  ): Promise<ClientProfile> => {
    if (!options.enableMutations) {
      throw new Error('Profile mutations not enabled');
    }

    if (profile) {
      // Profile exists, update it
      return await updateProfileMutation.mutateAsync(data as ClientProfileUpdate);
    } else {
      // Profile doesn't exist, create it
      // Set suppress toast flag if requested
      suppressCreateToastRef.current = saveOptions?.suppressToast ?? false;
      return await createProfileMutation.mutateAsync(data as Omit<ClientProfileInsert, 'user_id'>);
    }
  };

  return {
    // Data
    clientProfile: profile,
    isLoading: profileQuery.isLoading,
    error: profileQuery.error,
    
    // Permissions
    isOwner,
    
    // Mutations (only available if enabled)
    createClientProfile: options.enableMutations ? createProfileMutation.mutate : undefined,
    updateClientProfile: options.enableMutations ? updateProfileMutation.mutate : undefined,
    saveClientProfile: options.enableMutations ? saveProfile : undefined,
    isCreating: createProfileMutation.isPending,
    isUpdating: updateProfileMutation.isPending,
    isSaving: createProfileMutation.isPending || updateProfileMutation.isPending,
  };
}